// components/ui/Toast.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

const Toast = ({ 
  type = 'info', 
  title, 
  message, 
  duration = 5000, 
  onClose,
  isVisible = false 
}) => {
  const [isShowing, setIsShowing] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsShowing(true);
      // Start animation shortly after showing
      const animationTimer = setTimeout(() => setIsAnimating(true), 50);
      
      // Auto hide after duration
      const hideTimer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => {
        clearTimeout(animationTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [isVisible, duration]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsShowing(false);
      onClose?.();
    }, 200); // Wait for animation to complete
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-400" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-blue-400" />;
    }
  };

  const getStyles = () => {
    const baseStyles = "border-l-4 bg-gray-900/95 backdrop-blur-sm shadow-lg";
    
    switch (type) {
      case 'success':
        return `${baseStyles} border-green-400 text-green-100`;
      case 'error':
        return `${baseStyles} border-red-400 text-red-100`;
      case 'warning':
        return `${baseStyles} border-yellow-400 text-yellow-100`;
      case 'info':
      default:
        return `${baseStyles} border-blue-400 text-blue-100`;
    }
  };

  if (!isShowing) return null;

  return (
    <div className={`
      fixed top-4 right-4 z-50 min-w-80 max-w-md p-3 rounded-lg
      transition-all duration-200 ease-out
      ${isAnimating ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      ${getStyles()}
    `}>
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            {title && (
              <h4 className="text-sm font-semibold text-white">
                {typeof title === 'object' ? JSON.stringify(title) : title}
              </h4>
            )}
            {message && title && <span className="text-gray-400">•</span>}
            {message && (
              <p className="text-sm text-gray-300">
                {typeof message === 'object' ? JSON.stringify(message) : message}
              </p>
            )}
          </div>
        </div>
        
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-gray-400 hover:text-white transition-colors duration-150"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Toast;